import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { Observable, switchMap, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';

/**
 * Service for handling booking-related actions
 */
@Injectable({
  providedIn: 'root'
})
export class BookingActionService {
  private readonly bookingClientService = inject(BookingClientService);

  /**
   * Send entry code via email
   * @param bookingId Booking identifier
   * @returns Observable for API response
   */
  sendEntryCodeEmail(bookingId: number): Observable<any> {
    return this.bookingClientService.sendEntryCodeEmail(bookingId);
  }

  /**
   * Send entry code via SMS
   * @param bookingId Booking identifier
   * @returns Observable for API response
   */
  sendEntryCodeSms(bookingId: number): Observable<any> {
    return this.bookingClientService.sendEntryCodeSms(bookingId);
  }

  /**
   * Generate invoice for a booking
   * @param bookingId Booking identifier
   * @returns Observable for API response
   */
  generateInvoice(bookingId: number): Observable<any> {
    return this.bookingClientService.generateInvoice(bookingId);
  }

  /**
   * Check out a guest
   * @param bookingId Booking identifier
   * @returns Observable for API response
   */
  checkOut(bookingId: number): Observable<any> {
    return this.bookingClientService.checkoutBooking(bookingId);
  }

  /**
   * Cancel check-out of a guest
   * @param bookingId Booking identifier
   * @returns Observable for API response
   */
  cancelCheckOut(bookingId: number): Observable<any> {
    return this.bookingClientService.unCheckoutBooking(bookingId);
  }

  /**
   * Assign a checkin to a booking (paste checkin operation)
   * First loads the target booking to verify it doesn't already have a checkin
   * @param bookingId Target booking identifier
   * @param checkinIri Checkin IRI to assign (full IRI path)
   * @returns Observable with updated booking data
   * @throws Error with code 'ALREADY_CHECKED_IN' if booking already has a checkin
   */
  assignCheckinToBooking(bookingId: number, checkinIri: string): Observable<IBookingModel> {
    return this.bookingClientService.getItem(bookingId).pipe(
      switchMap((booking) => {
        if (booking.checkin) {
          return throwError(() => new Error('ALREADY_CHECKED_IN'));
        }
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const offset = now.getTimezoneOffset();
        const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
        const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
        const offsetSign = offset <= 0 ? '+' : '-';
        const checkinTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
        return this.bookingClientService.updateItem({
          id: bookingId,
          checkin: checkinIri,
          checked_in: true,
          checkin_time: checkinTime
        } as IBookingModel);
      })
    );
  }
}
